import type { Region } from '@experiences/constants';

import type {
    IEarliestMigrationRequest,
    IFlexibleMigrationRequest,
    IMigrationRequest,
    IMigrationRequestBase,
    IScheduleSlot,
} from '../common/interfaces/gws';

const formatHour = (hour: number): string => {
    const formattedHour = hour < 10 ? `0${hour}` : `${hour}`;
    return `${formattedHour}:00`;
};

/**
 * This function generates a list of time ranges from 00:00 to 20:00, in 4-hour intervals.
 * @returns a list of time ranges
 */
export const generateTimeWindows = () => {
    const hours = Array.from({ length: 21 }, (_, i) => i);

    const hoursList = hours.map((hour) => {
        const nextHour = hour === 20 ? 0 : hour + 4;
        const nextTime = formatHour(nextHour);
        const curTime = formatHour(hour);

        return `${curTime} - ${nextTime}`;
    });

    return hoursList;
};

// ts-unused-exports:disable-next-line
export type TranslateFn = (descriptor: { id: string }, values?: Record<string, string | number>) => string;

/**
 * Detects the browser timezone information.
 */
export const getBrowserTimezone = (): {
    name: string;
    short: string;
    utcOffset: string;
    offsetMinutes: number;
} => {
    try {
        const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = new Date();
        const offsetMinutes = -now.getTimezoneOffset();
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const absOffset = Math.abs(offsetMinutes);
        const offsetHours = Math.floor(absOffset / 60);
        const offsetMins = absOffset % 60;
        const offsetMinsSuffix = offsetMins > 0 ? `:${offsetMins.toString().padStart(2, '0')}` : '';
        const utcOffset = `UTC${sign}${offsetHours}${offsetMinsSuffix}`;
        const short = now.toLocaleTimeString('en-US', { timeZoneName: 'short' })
            .split(' ')
            .pop() ?? utcOffset;

        return {
            name,
            short,
            utcOffset,
            offsetMinutes,
        };
    } catch {
        return {
            name: 'UTC',
            short: 'UTC',
            utcOffset: 'UTC+0',
            offsetMinutes: 0,
        };
    }
};

/**
 * Converts a UTC hour to local time given an offset in minutes.
 */
export const utcHourToLocal = (utcHour: number, offsetMinutes: number): {
    totalMin: number;
    timeStr: string;
} => {
    let totalMin = utcHour * 60 + offsetMinutes;
    if (totalMin < 0) {
        totalMin += 24 * 60;
    }
    if (totalMin >= 24 * 60) {
        totalMin -= 24 * 60;
    }
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const amPm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    const timeStr = m === 0
        ? `${displayHour}:00 ${amPm}`
        : `${displayHour}:${m.toString().padStart(2, '0')} ${amPm}`;
    return {
        totalMin,
        timeStr,
    };
};

export interface TimeWindow {
    utcValue: string;
    localLabel: string;
    utcLabel: string;
    localStartMin: number;
}

/**
 * Generates timezone-aware time windows.
 */
export const generateLocalizedTimeWindows = (
    offsetMinutes: number,
    tzShort: string,
    translate?: TranslateFn,
): TimeWindow[] => {
    const baseWindows = generateTimeWindows();

    return baseWindows.map(utcValue => {
        const [ start, end ] = utcValue.split(' - ');
        const startHour = Number.parseInt(start, 10);
        const endHour = Number.parseInt(end, 10);

        const localStart = utcHourToLocal(startHour, offsetMinutes);
        const localEnd = utcHourToLocal(endHour, offsetMinutes);

        const nextDaySuffix = localEnd.totalMin <= localStart.totalMin
            ? ` ${translate ? translate({ id: 'CLIENT_MIGRATION_NEXT_DAY_SUFFIX' }) : '(next day)'}`
            : '';
        const localLabel = `${localStart.timeStr} - ${localEnd.timeStr}${nextDaySuffix} ${tzShort}`;
        const utcLabel = `${utcValue} UTC`;

        return {
            utcValue,
            localLabel,
            utcLabel,
            localStartMin: localStart.totalMin,
        };
    });
};

/**
 * Parses a "HH:00 - HH:00" string into start/end hours.
 */
export const parseHourWindow = (hourStr: string): { startHour: number; endHour: number } => {
    const [ start, end ] = hourStr.split(' - ');
    return {
        startHour: Number.parseInt(start, 10),
        endHour: Number.parseInt(end, 10),
    };
};

export const hoursToTicks = (hours: number): number => hours * 60 * 60 * 1e7;

export const ticksToHours = (ticks: number): number => ticks / (60 * 60 * 1e7);

/** Returns 'YYYY-MM-DD' for a Date */
export const toISODate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export type ScheduleMode = 'earliest' | 'flexible';

export const MIGRATION_STATUS_I18N: Record<string, string> = {
    Migrated: 'CLIENT_MIGRATION_SERVICE_STATUS_MIGRATED',
    Running: 'CLIENT_MIGRATION_SERVICE_STATUS_RUNNING',
    Failed: 'CLIENT_MIGRATION_SERVICE_STATUS_FAILED',
    Pending: 'CLIENT_MIGRATION_SERVICE_STATUS_PENDING',
    AwaitingFix: 'CLIENT_MIGRATION_SERVICE_STATUS_AWAITING_FIX',
};

export const MIGRATION_ERROR_CODE_TO_I18N: Record<string, string> = {
    MIGRATION_BLOCKED_REQUIRES_RERUN: 'CLIENT_MIGRATION_ERROR_REQUIRES_RERUN',
    MIGRATION_BLOCKED_REQUIRES_MITIGATION: 'CLIENT_MIGRATION_ERROR_REQUIRES_MITIGATION',
    MIGRATION_ALREADY_RUNNING: 'CLIENT_MIGRATION_ERROR_ALREADY_RUNNING',
    MIGRATION_SERVICE_LOCK_CONFLICT: 'CLIENT_MIGRATION_ERROR_SERVICE_LOCK',
    MIGRATION_ALREADY_COMPLETED: 'CLIENT_MIGRATION_ERROR_ALREADY_COMPLETED',
    MIGRATION_ALREADY_SCHEDULED: 'CLIENT_MIGRATION_ERROR_ALREADY_SCHEDULED',
    NO_SERVICES_TO_MOVE: 'CLIENT_MIGRATION_ERROR_NO_SERVICES',
    INVALID_SOURCE_REGION: 'CLIENT_MIGRATION_ERROR_INVALID_SOURCE',
    FEATURE_DISABLED: 'CLIENT_MIGRATION_ERROR_FEATURE_DISABLED',
};

/**
 * Returns the next upcoming slot date from a list of schedule slots.
 * Finds the first slot whose date is >= today and is not expired.
 * Returns undefined if no upcoming slot exists.
 */
export const getNextSlotDate = (slots: IScheduleSlot[] | undefined): string | undefined => {
    if (!slots || slots.length === 0) {
        return undefined;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = toISODate(today);

    // slotDate is always YYYY-MM-DD format, so lexicographic comparison is chronologically correct
    const upcoming = slots
        .filter(s => !s.isExpired && s.slotDate >= todayStr)
        .sort((a, b) => a.slotDate.localeCompare(b.slotDate));

    return upcoming.length > 0 ? upcoming[0].slotDate : undefined;
};

// ts-unused-exports:disable-next-line
export type SchedulePayloadParams = {
    sourceRegion: Region;
    destinationRegion: Region;
    hour: string;
} & (
    | { scheduleMode: 'earliest'; scheduleStartDate: string; scheduleEndDate: string; customDates?: never }
    | { scheduleMode: 'flexible'; customDates: string[]; scheduleStartDate?: never; scheduleEndDate?: never }
);

/**
 * Builds a migration request payload from form values.
 */
export const buildSchedulePayload = (params: SchedulePayloadParams): IMigrationRequest => {
    const {
        startHour,
        endHour,
    } = parseHourWindow(params.hour);
    const base: IMigrationRequestBase = {
        sourceRegion: params.sourceRegion,
        destinationRegion: params.destinationRegion,
        timeOfDayStartIntervalTicks: hoursToTicks(startHour),
        timeOfDayEndIntervalTicks: endHour === 0 ? hoursToTicks(24) : hoursToTicks(endHour),
    };

    if (params.scheduleMode === 'earliest') {
        return {
            ...base,
            scheduleType: 'Simple',
            scheduleStartDate: params.scheduleStartDate,
            scheduleEndDate: params.scheduleEndDate,
        } satisfies IEarliestMigrationRequest;
    }

    return {
        ...base,
        scheduleType: 'Flexible',
        slots: params.customDates,
    } satisfies IFlexibleMigrationRequest;
};
